import React, { FC, useEffect, useState } from 'react';
import { CropperTabletMobileProps } from '.';
import { Element, notchBottom, notchTop, scale } from '@components/frame';
import { isPhone } from '@helpers';
import { CropView } from 'react-native-image-crop-tools';
import { View, Image } from 'react-native';
import { SmallLoading } from '@components/loading';
import { onCropMobileTablet } from '@v2/components/imageHandling/components/Cropper/helpers';
import { calculateCropDimensions } from '@reducers/v2/imageHandlingV2/thunks/parseAndAddUrlsToImagesDocuments';

export interface IOSCropperProps extends CropperTabletMobileProps {
  base64Original: string | null;
}

export const IOSCropper: FC<IOSCropperProps> = ({
  image,
  cropRef,
  angle,
  onSave,
  triggerShow,
  base64Original,
  modeRef,
  triggerDiscardChangesPopup,
  setShowChooseCropModeModalOpen,
  setPendingCropData,
  isNewImage = false,
}) => {
  const [actualImageDimensions, setActualImageDimensions] = useState<{ width: number; height: number } | null>(null);
  const [initialCropFrame, setInitialCropFrame] = useState<{ x: number; y: number; width: number; height: number } | null>(null);
  const tabletCropInset = isPhone ? 0 : 40;

  useEffect(() => {
    if (base64Original) {
      Image.getSize(
        base64Original,
        (width, height) => {
          setActualImageDimensions({ width, height });

          // Calculate initial crop frame based on cropSettings if image is cropped
          if (image.isCropped && image.cropSettings?.length > 0) {
            const cropDimensions = calculateCropDimensions({
              width,
              height,
              cropSettings: image.cropSettings,
              pageNumber: image.pageNumber || 1,
              customAngle: angle,
            });
            setInitialCropFrame(cropDimensions);
          } else if (image.isCropped) {
            // If cropped but no cropSettings, use stored crop dimensions
            const { width: imageWidth = image.widthOriginal, height: imageHeight = image.heightOriginal, x = 0, y = 0 } = image;
            const frame = { x, y, width: imageWidth, height: imageHeight };

            setInitialCropFrame(frame);
          } else {
            // If not cropped at all, use full image dimensions
            setInitialCropFrame({ x: 0, y: 0, width, height });
          }
        },
        (error) => {
          console.error('Error getting image size:', error);
          // Fallback to original dimensions
          const { width: imageWidth = image.widthOriginal, height: imageHeight = image.heightOriginal, x = 0, y = 0 } = image;
          setInitialCropFrame({ x, y, width: imageWidth, height: imageHeight });
        }
      );
    }
  }, [base64Original, image, angle]);

  return (
    <Element
      {...{
        config: {
          top: isPhone ? 51.5 + notchTop : 74,
          bottom: isPhone ? 52 + notchBottom : tabletCropInset,
          right: isPhone ? 0 : tabletCropInset,
          left: isPhone ? 0 : tabletCropInset,
        },
      }}
    >
      {!base64Original && (
        <View style={{ marginTop: scale(40) }}>
          <SmallLoading />
        </View>
      )}
      {base64Original && initialCropFrame && (
        <CropView
          sourceUrl={base64Original}
          style={{ flex: 1, width: '100%' }}
          ref={cropRef}
          onImageCrop={(res: any) =>
            onCropMobileTablet({
              angle,
              calculations: res,
              image: actualImageDimensions
                ? { ...image, widthOriginal: actualImageDimensions.width, heightOriginal: actualImageDimensions.height }
                : image,
              mode: modeRef.current,
              onSave,
              triggerDiscardChangesPopup,
              triggerShow,
              actualImageDimensions,
              setShowChooseCropModeModalOpen,
              setPendingCropData,
              isNewImage,
            })
          }
          {...(image.isCropped ? { initialImageCropFrame: initialCropFrame } : {})}
          angle={angle || 0}
        />
      )}
    </Element>
  );
};
